import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  Download,
  Check,
  X,
  Loader2,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Champs disponibles pour le mapping
const AVAILABLE_FIELDS = [
  { key: 'name', label: 'Nom *', required: true },
  { key: 'model', label: 'Modèle' },
  { key: 'description', label: 'Description' },
  { key: 'base_price', label: 'Prix de base' },
  { key: 'weight', label: 'Poids' },
  { key: 'power', label: 'Puissance' },
  { key: 'brand', label: 'Marque' },
  { key: 'warranty', label: 'Garantie' },
  { key: 'material_type_id', label: 'Type de matériel' },
  { key: 'longueur', label: 'Longueur' },
  { key: 'largeur', label: 'Largeur' },
  { key: 'profondeur_fouille', label: 'Profondeur de fouille' },
  { key: 'hauteur_chargement', label: 'Hauteur de chargement' },
  { key: 'marque_moteur', label: 'Marque moteur' },
  { key: 'nombre_cylindres', label: 'Nombre de cylindres' },
  { key: 'puissance_moteur', label: 'Puissance moteur' },
  { key: 'marque_pompe', label: 'Marque pompe' },
  { key: 'debit_pompe', label: 'Débit pompe' },
  { key: 'image_url', label: 'URL Image' },
  { key: 'brochure_url', label: 'URL Brochure' },
  { key: 'visible_on_website', label: 'Visible sur le portail' },
  { key: 'website_description', label: 'Description portail' },
];

export default function CatalogueImportDialog({ open, onOpenChange, onImportSuccess }) {
  const [step, setStep] = useState(1); // 1: Upload, 2: Mapping, 3: Preview, 4: Result
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Veuillez sélectionner un fichier CSV');
      return;
    }

    setFile(selectedFile);
    
    // Upload for preview
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mini-pelles/import/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) throw new Error('Preview failed');

      const data = await response.json();
      setPreviewData(data);
      
      // Auto-map columns based on names
      const autoMapping = {};
      AVAILABLE_FIELDS.forEach(field => {
        const matchingCol = data.columns.find(col => 
          col.toLowerCase().replace(/[_\s]/g, '') === field.key.toLowerCase().replace(/[_\s]/g, '') ||
          col.toLowerCase().includes(field.key.toLowerCase())
        );
        if (matchingCol) {
          autoMapping[field.key] = matchingCol;
        }
      });
      setMapping(autoMapping);
      setStep(2);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Erreur lors de la lecture du fichier');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mini-pelles/import/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_import_catalogue.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleImport = async () => {
    // Validate required fields
    if (!mapping.name) {
      toast.error('Le champ "Nom" est obligatoire');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/mini-pelles/import/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) throw new Error('Import failed');

      const result = await response.json();
      setImportResult(result);
      setStep(4);
      
      if (result.imported > 0 || result.updated > 0) {
        toast.success(result.message);
        onImportSuccess?.();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const resetDialog = () => {
    setStep(1);
    setFile(null);
    setPreviewData(null);
    setMapping({});
    setImportResult(null);
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-yellow-500" />
            Import du Catalogue
          </DialogTitle>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-yellow-500 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 4 && <div className={`w-12 h-1 mx-1 ${step > s ? 'bg-yellow-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg hover:border-yellow-500 transition cursor-pointer"
                 onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-700">Cliquez pour sélectionner un fichier CSV</p>
              <p className="text-sm text-slate-500 mt-2">ou glissez-déposez votre fichier ici</p>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".csv"
                onChange={handleFileSelect}
              />
            </div>

            <div className="flex justify-center">
              <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2">
                <Download className="w-4 h-4" />
                Télécharger le modèle de fichier
              </Button>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-medium text-slate-800 mb-2">Format attendu :</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Fichier CSV avec séparateur point-virgule (;)</li>
                <li>• Première ligne = en-têtes de colonnes</li>
                <li>• Encodage UTF-8 recommandé</li>
                <li>• Le champ <strong>nom</strong> est obligatoire</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Mapping */}
        {step === 2 && previewData && (
          <div className="space-y-6">
            <div className="bg-yellow-50 p-4 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>{previewData.total_rows}</strong> lignes détectées dans <strong>{previewData.filename}</strong>
              </p>
            </div>

            <div>
              <h4 className="font-medium text-slate-800 mb-4">Associez les colonnes de votre fichier aux champs du catalogue :</h4>
              
              <div className="grid md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto p-2">
                {AVAILABLE_FIELDS.map((field) => (
                  <div key={field.key} className="flex items-center gap-3">
                    <Label className="w-40 text-sm">
                      {field.label}
                      {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    <Select
                      value={mapping[field.key] || 'none'}
                      onValueChange={(v) => setMapping({ ...mapping, [field.key]: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Non mappé --</SelectItem>
                        {previewData.columns.map((col) => (
                          <SelectItem key={col} value={col}>{col}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {mapping[field.key] && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                Retour
              </Button>
              <Button onClick={() => setStep(3)} disabled={!mapping.name} className="gap-2 bg-yellow-500 hover:bg-yellow-600">
                Aperçu <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && previewData && (
          <div className="space-y-6">
            <h4 className="font-medium text-slate-800">Aperçu des données (5 premières lignes) :</h4>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    {Object.entries(mapping).filter(([k, v]) => v).map(([field, col]) => (
                      <th key={field} className="px-3 py-2 text-left font-medium text-slate-700">
                        {AVAILABLE_FIELDS.find(f => f.key === field)?.label || field}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.slice(0, 5).map((row, i) => (
                    <tr key={i} className="border-t">
                      {Object.entries(mapping).filter(([k, v]) => v).map(([field, col]) => (
                        <td key={field} className="px-3 py-2 text-slate-600">
                          {row[col] || <span className="text-slate-300">-</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note :</strong> Les machines existantes (même nom ou modèle) seront mises à jour.
              </p>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                Modifier le mapping
              </Button>
              <Button onClick={handleImport} disabled={importing} className="gap-2 bg-yellow-500 hover:bg-yellow-600">
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Importer {previewData.total_rows} ligne(s)
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 4 && importResult && (
          <div className="space-y-6 text-center py-8">
            {importResult.imported > 0 || importResult.updated > 0 ? (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800">Import terminé !</h3>
                <div className="flex justify-center gap-4">
                  <Badge className="bg-green-100 text-green-700 px-4 py-2">
                    {importResult.imported} créée(s)
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-700 px-4 py-2">
                    {importResult.updated} mise(s) à jour
                  </Badge>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                  <AlertTriangle className="w-8 h-8 text-yellow-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800">Aucune donnée importée</h3>
              </>
            )}

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="bg-red-50 p-4 rounded-lg text-left">
                <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Erreurs ({importResult.errors.length})
                </h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {importResult.errors.map((err, i) => (
                    <li key={i}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={resetDialog} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Nouvel import
              </Button>
              <Button onClick={handleClose} className="bg-yellow-500 hover:bg-yellow-600">
                Fermer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
